import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import {LogsServices} from "../../services/LogsServices";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#845EC2"];

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border shadow-sm ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

const Input = ({ placeholder, value, onChange, className = "" }) => (
  <input
    type="text"
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
  />
);

const Button = ({
                  children,
                  onClick,
                  variant = "default",
                  className = "",
                }) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 h-10 px-4 py-2";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    outline:
      "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

const Table = ({ children, className = "" }) => (
  <table className={`w-full border-collapse ${className}`}>{children}</table>
);

const TableHeader = ({ children }) => <thead>{children}</thead>;
const TableBody = ({ children }) => <tbody>{children}</tbody>;
const TableRow = ({ children }) => (
  <tr className="border-b border-gray-200 hover:bg-gray-50">{children}</tr>
);
const TableHead = ({ children }) => (
  <th className="h-12 px-4 text-left align-middle font-medium text-gray-700 bg-gray-50">
    {children}
  </th>
);
const TableCell = ({ children, className = "" }) => (
  <td className={`p-4 align-middle text-gray-900 ${className}`}>{children}</td>
);

const ViewLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await LogsServices.fetchLogs();
        setLogs(response);
      } catch (error) {
        console.error("Erro ao carregar logs:", error);
      }
    };
    fetchLogs();
  }, []);

  const formatDate = (epoch) => {
    return new Date(epoch).toLocaleString("pt-BR");
  };

  const filteredLogs = logs.filter(
    (log) =>
      log.email?.toLowerCase().includes(filter.toLowerCase()) ||
      log.action?.toLowerCase().includes(filter.toLowerCase())
  );

  const totalLogs = logs.length;
  const logins = logs.filter((l) =>
    l.action?.toLowerCase().includes("login")
  ).length;
  const navegacoes = logs.filter((l) =>
    l.action?.toLowerCase().includes("navegação:")
  ).length;
  const arquivos = logs.filter((l) =>
    l.action?.toLowerCase().includes("upload:")
  ).length;


  const actionsCount = logs.reduce((acc, log) => {
    const key = log.action?.split(":")[0] || "Outro";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const actionData = Object.entries(actionsCount).map(([name, value]) => ({
    name,
    value,
  }));

  const timeData = logs.reduce((acc, log) => {
    const day = new Date(log.epochTimestamp).toLocaleDateString("pt-BR");
    acc[day] = (acc[day] || 0) + 1;
    return acc;
  }, {});
  const timelineData = Object.entries(timeData).map(([day, value]) => ({
    day,
    value,
  }));

  const reportAccessCount = logs.reduce((acc, log) => {
    const regex = /^Ação: Acesso ao relatório (.+)$/;
    const match = log.action?.match(regex);
    if (match) {
      const reportName = match[1];
      acc[reportName] = (acc[reportName] || 0) + 1;
    }
    return acc;
  }, {});

  const reportData = Object.entries(reportAccessCount)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);


  return (
    <div className="p-6 space-y-6 bg-gray-100 min-h-screen w-full">
      <h1 className="text-2xl font-bold text-gray-800">📊 Análise de Logs</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Total de Logs</p>
            <p className="text-xl font-bold text-gray-800">{totalLogs}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Logins</p>
            <p className="text-xl font-bold text-blue-600">{logins}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Navegações</p>
            <p className="text-xl font-bold text-green-600">{navegacoes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Arquivos Processados</p>
            <p className="text-xl font-bold text-orange-600">{arquivos}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold mb-2 text-gray-800">
              Distribuição de Ações
            </h2>
            {actionData.length === 0 ? (
              <p className="text-center text-gray-500">Nenhum dado disponível</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={actionData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label
                  >
                    {actionData.map((_, index) => (
                      <Cell
                        key={index}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold mb-2 text-gray-800">
              Evolução dos Logs
            </h2>
            {timelineData.length === 0 ? (
              <p className="text-center text-gray-500">Nenhum dado disponível</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8884d8"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <h2 className="font-semibold mb-2 text-gray-800">Relatórios mais acessados</h2>
          {reportData.length === 0 ? (
            <p className="text-gray-500">Nenhum acesso a relatórios registrado.</p>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={reportData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={200} />
                <Tooltip />
                <Bar dataKey="value" fill="#845EC2" />
              </BarChart>
            </ResponsiveContainer>
          )}

        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Filtrar por email ou ação..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="flex-1"
            />
            <Button variant="outline" onClick={() => setFilter("")}>
              Limpar
            </Button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">
                      {log.email}
                    </TableCell>
                    <TableCell>
                      {formatDate(log.epochTimestamp)}
                    </TableCell>
                    <TableCell className="max-w-md truncate">
                      {log.action}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredLogs.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">Nenhum log encontrado</p>
                <p className="text-gray-400 text-sm mt-1">
                  Tente ajustar os filtros de busca
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewLogs;
